/**
 * Opt-in debug log of the point-cloud renderer, for a user to send back from
 * their browser when a cloud loads (requests in the Network tab) but draws
 * nothing or only a few points.
 *
 * Turn it on, then reload:
 *   localStorage.setItem('pointcloudDebug', '1')
 * or add `?pointcloudDebug=1` to the page URL. Off again:
 *   localStorage.removeItem('pointcloudDebug')
 *
 * or, without a reload, `__pointcloudDebug.on()` in the console.
 *
 * Every 2 s the viewer logs one row per loaded cloud (`console.table`), and in
 * the console `__pointcloudDebug.copy()` puts a fresh snapshot plus the recent
 * fetch failures (JSON) on the clipboard; `.dump()` returns the string;
 * `.clouds` holds the live `PointCloudOctree`s for poking at by hand;
 * `.frames` counts the viewer's point-cloud frames (0 = that loop never ran).
 *
 * Off (the default) it costs a counter and one boolean test per frame.
 */

/** Seconds between two logged snapshots. */
export const POINTCLOUD_DEBUG_INTERVAL_MS = 2000;
/** Fetch failures kept (the oldest are dropped). */
const FAILURE_RING_SIZE = 200;

let enabled: boolean | null = null;

/** Read once per page load: URL `?pointcloudDebug=`, else localStorage `pointcloudDebug`. */
export function pointcloudDebugEnabled(): boolean {
  if (enabled !== null) return enabled;
  let raw: string | null = null;
  try {
    if (typeof location !== 'undefined') raw = new URLSearchParams(location.search).get('pointcloudDebug');
  } catch {
    /* no URL */
  }
  if (raw == null) {
    try {
      raw = typeof localStorage !== 'undefined' ? localStorage.getItem('pointcloudDebug') : null;
    } catch {
      /* storage blocked */
    }
  }
  enabled = raw != null && raw !== '0' && raw !== 'false';
  return enabled;
}

/** Tests only: forget the cached switch. */
export function resetPointcloudDebugForTests() {
  enabled = null;
  failures.length = 0;
}

/** The parts of a three-loader node the snapshot reads (all optional: v1 and v2 differ). */
interface NodeLike {
  loaded?: boolean;
  loading?: boolean;
  failed?: boolean;
  numPoints?: number;
  level?: number;
  geometry?: { attributes?: Record<string, { array?: ArrayLike<number>; itemSize?: number }> };
  sceneNode?: { visible?: boolean; geometry?: { attributes?: Record<string, { array?: ArrayLike<number>; itemSize?: number }> } };
}

/** The parts of a `PointCloudOctree` the snapshot reads. */
export interface PointCloudLike {
  visible: boolean;
  userData: Record<string, unknown>;
  numVisiblePoints?: number;
  visibleNodes?: NodeLike[];
  visibleGeometry?: NodeLike[];
  minNodePixelSize?: number;
  matrixWorld?: { elements: ArrayLike<number> };
  pcoGeometry?: {
    numNodesLoading?: number;
    maxNumNodesLoading?: number;
    root?: NodeLike;
    loader?: { metadata?: { attributes?: { name: string }[] } };
  };
  material?: {
    pointColorType?: number;
    customAttributeName?: string;
    customScalarRange?: [number, number];
    useFilterByClassification?: boolean;
    clipMode?: number;
    numClipBoxes?: number;
    opacity?: number;
    size?: number;
    pointSizeType?: number;
    visible?: boolean;
  };
}

/** Min / max / non-finite count of one attribute over the visible nodes' geometry. */
function attributeStats(nodes: NodeLike[], name: string) {
  let min = Infinity;
  let max = -Infinity;
  let nonFinite = 0;
  let count = 0;
  for (const n of nodes) {
    const attr = n.sceneNode?.geometry?.attributes?.[name] ?? n.geometry?.attributes?.[name];
    const arr = attr?.array;
    if (!arr) continue;
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      count++;
      if (!Number.isFinite(v)) nonFinite++;
      else {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  return count === 0 ? null : { min: count > nonFinite ? min : null, max: count > nonFinite ? max : null, nonFinite, count };
}

/**
 * A plain-JSON picture of one cloud: what Potree decided to show this frame,
 * what is still loading, and the material state that decides whether the
 * shown nodes draw anything.
 */
export function snapshotPointCloud(pc: PointCloudLike) {
  const visibleNodes = pc.visibleNodes ?? [];
  const visibleGeometry = pc.visibleGeometry ?? [];
  const geom = pc.pcoGeometry;
  const m = pc.material ?? {};
  const e = pc.matrixWorld?.elements;
  const firstGeometry = visibleNodes.find((n) => n.sceneNode?.geometry?.attributes)?.sceneNode?.geometry?.attributes;
  return {
    layerId: pc.userData.layerId as string | undefined,
    visible: pc.visible,
    numVisiblePoints: pc.numVisiblePoints ?? null,
    visibleNodes: visibleNodes.length,
    visibleNodesShown: visibleNodes.filter((n) => n.sceneNode?.visible !== false).length,
    maxVisibleLevel: visibleNodes.reduce((a, n) => Math.max(a, n.level ?? -1), -1),
    wantedNodes: visibleGeometry.length,
    wantedLoading: visibleGeometry.filter((n) => n.loading).length,
    wantedFailed: visibleGeometry.filter((n) => n.failed).length,
    wantedNotLoaded: visibleGeometry.filter((n) => !n.loaded && !n.loading).length,
    numNodesLoading: geom?.numNodesLoading ?? null,
    maxNumNodesLoading: geom?.maxNumNodesLoading ?? null,
    rootLoaded: geom?.root?.loaded ?? null,
    metadataAttributes: geom?.loader?.metadata?.attributes?.map((a) => a.name) ?? null,
    nodeAttributes: firstGeometry ? Object.keys(firstGeometry) : null,
    customScalarStats: m.customAttributeName ? attributeStats(visibleNodes, m.customAttributeName) : null,
    minNodePixelSize: pc.minNodePixelSize ?? null,
    position: e ? [e[12], e[13], e[14]] : null,
    scale: e ? Math.hypot(e[0], e[1], e[2]) : null,
    material: {
      visible: m.visible ?? null,
      pointColorType: m.pointColorType ?? null,
      customAttributeName: m.customAttributeName ?? null,
      customScalarRange: m.customScalarRange ? [...m.customScalarRange] : null,
      useFilterByClassification: m.useFilterByClassification ?? null,
      clipMode: m.clipMode ?? null,
      numClipBoxes: m.numClipBoxes ?? null,
      opacity: m.opacity ?? null,
      size: m.size ?? null,
      pointSizeType: m.pointSizeType ?? null,
    },
  };
}

export type PointCloudSnapshot = ReturnType<typeof snapshotPointCloud>;

type FailureEntry = { url?: string; range?: string; status?: number; error?: string; kind?: string };
const failures: (FailureEntry & { t: number })[] = [];

/** Recorded by `checkedPointCloudFetch` (fetches) and `sharedWorkerPool` (decodes) while the debug log is on. */
export function recordPointcloudFetchFailure(entry: FailureEntry) {
  if (!pointcloudDebugEnabled()) return;
  failures.push({ t: Math.round(performance.now()), ...entry });
  if (failures.length > FAILURE_RING_SIZE) failures.shift();
}

let lastLog = -Infinity;
let latest: PointCloudSnapshot[] = [];
let liveClouds: readonly object[] = [];
let lastExtra: Record<string, unknown> = {};
/** Frames the viewer's point-cloud loop has run (counted even with the log off: one increment). */
let frames = 0;
/** Pool stats for the dump (`sharedWorkerPool` registers it). */
let poolStats: (() => unknown) | null = null;
export function registerPointcloudPoolStats(stats: () => unknown) {
  poolStats = stats;
}

/**
 * Always on the page (a few fields, no work): `__pointcloudDebug.frames` tells
 * "the point-cloud loop is not running" apart from "the log is off", and
 * `.on()` switches the log on without a reload.
 */
const api = {
  get enabled() {
    return pointcloudDebugEnabled();
  },
  get frames() {
    return frames;
  },
  get clouds() {
    return liveClouds;
  },
  get snapshots() {
    return latest;
  },
  failures,
  on() {
    try {
      localStorage.setItem('pointcloudDebug', '1');
    } catch {
      /* storage blocked: on for this page only */
    }
    enabled = true;
    lastLog = -Infinity;
  },
  /** A fresh snapshot of every cloud, as JSON. */
  dump: () =>
    JSON.stringify(
      {
        at: Math.round(performance.now()),
        frames,
        extra: lastExtra,
        decoderPool: poolStats?.() ?? null,
        snapshots: (liveClouds as readonly PointCloudLike[]).map(snapshotPointCloud),
        failures,
      },
      null,
      2,
    ),
  copy: () => navigator.clipboard?.writeText(api.dump()),
};
(globalThis as unknown as { __pointcloudDebug?: typeof api }).__pointcloudDebug = api;

/** Called at the top of every frame of the viewer's point-cloud loop; logs at most every `POINTCLOUD_DEBUG_INTERVAL_MS`. */
export function tickPointcloudDebug(clouds: readonly object[], extra: Record<string, unknown> = {}) {
  frames++;
  liveClouds = clouds;
  if (!pointcloudDebugEnabled()) return;
  const t = performance.now();
  if (t - lastLog < POINTCLOUD_DEBUG_INTERVAL_MS) return;
  lastLog = t;
  lastExtra = extra;
  // Read through the structural view: three-loader's own types are stricter than this log needs.
  latest = (clouds as readonly PointCloudLike[]).map(snapshotPointCloud);
  console.groupCollapsed(`[pointcloudDebug] ${clouds.length} cloud(s), ${failures.length} fetch failure(s)`, extra);
  console.table(
    latest.map(({ material, position, metadataAttributes, nodeAttributes, customScalarStats, ...row }) => ({
      ...row,
      colour: material.pointColorType,
      attr: material.customAttributeName,
      range: material.customScalarRange?.join('..'),
      classFilter: material.useFilterByClassification,
      clip: `${material.clipMode}/${material.numClipBoxes}`,
      nodeAttrs: nodeAttributes?.join(','),
      scalar: customScalarStats ? `${customScalarStats.min}..${customScalarStats.max} nan:${customScalarStats.nonFinite}` : '',
      pos: position?.map((v) => Math.round(v)).join(','),
    })),
  );
  console.groupEnd();
}
